import { useEffect } from 'react'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { copyToClipboard } from '@/lib/copy-to-clipboard'

/**
 * Adds copy-to-clipboard buttons to all code blocks on the page.
 * Used for MDX content where we can't inject React components at build time.
 *
 * Pass a `contentKey` that identifies the currently rendered content (e.g. the
 * route pathname). The buttons are re-attached whenever it changes, because the
 * host component can persist across navigation while its content swaps out — a
 * mount-only effect would then only ever attach buttons to the first page.
 */
export function useCodeCopyButtons(contentKey?: string) {
  useEffect(() => {
    // Find all pre elements with language classes (Prism code blocks)
    const codeBlocks = document.querySelectorAll('pre[class*="language-"]')

    const buttons: HTMLButtonElement[] = []

    codeBlocks.forEach((pre) => {
      // Skip if already has a copy button
      if (pre.parentElement?.classList.contains('code-block-wrapper')) return

      // Wrap in a container for positioning
      const wrapper = document.createElement('div')
      wrapper.className = 'code-block-wrapper group relative'
      pre.parentNode?.insertBefore(wrapper, pre)
      wrapper.appendChild(pre)

      // Create the copy button
      const button = document.createElement('button')
      button.type = 'button'
      // The reveal is left to CSS rather than a matchMedia read: this class is
      // assigned once when the button is attached, so a hybrid device that
      // gains or loses a pointer afterwards would keep whichever branch it
      // happened to get. REVEAL_ON_HOVER also carries the pointer-events and
      // focus-within halves this hand-rolled version was missing.
      button.className =
        'code-copy-btn absolute right-2 top-2 z-10 rounded-md p-1.5 ' +
        'bg-[var(--muted)]/80 hover:bg-[var(--muted)] border border-[var(--border)]/50 ' +
        REVEAL_ON_HOVER
      button.setAttribute('aria-label', 'Copy code')
      button.setAttribute('title', 'Copy Code')
      button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="copy-icon"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="check-icon hidden text-emerald-500"><path d="M20 6 9 17l-5-5"/></svg>`

      button.addEventListener('click', async () => {
        const code = pre.querySelector('code')?.textContent || ''
        try {
          await copyToClipboard(code)
          button.querySelector('.copy-icon')?.classList.add('hidden')
          button.querySelector('.check-icon')?.classList.remove('hidden')
          button.setAttribute('aria-label', 'Copied!')
          button.setAttribute('title', 'Copied!')
          setTimeout(() => {
            button.querySelector('.copy-icon')?.classList.remove('hidden')
            button.querySelector('.check-icon')?.classList.add('hidden')
            button.setAttribute('aria-label', 'Copy code')
            button.setAttribute('title', 'Copy Code')
          }, 2000)
        } catch (err) {
          console.error('Failed to copy code:', err)
        }
      })

      wrapper.appendChild(button)
      buttons.push(button)
    })

    // Cleanup on unmount
    return () => {
      buttons.forEach((button) => {
        const wrapper = button.parentElement
        if (wrapper?.classList.contains('code-block-wrapper')) {
          const pre = wrapper.querySelector('pre')
          if (pre) {
            wrapper.parentNode?.insertBefore(pre, wrapper)
            wrapper.remove()
          }
        }
      })
    }
  }, [contentKey])
}
